import { FC, useEffect, useState } from "react"

import { Card, Row, Typography } from "antd"

import { Table } from "@/components/_Shared/Table"
import { AnalyticsAreaChart } from "@/components/Analytics/AnalyticsAreaChart"
import { AnalyticsBarChart } from "@/components/Analytics/AnalyticsBarChart"
import { AnalyticsLineChart } from "@/components/Analytics/AnalyticsLineChart"
import { AnalyticsPieChart } from "@/components/Analytics/AnalyticsPieChart"
import { useAnalyticsFilters } from "@/components/Analytics/AnalyticsSettings/useAnalyticsFilters"
import { useAnalyticsHelpers, ITotalNumberAnalytics } from "@/components/Analytics/useAnalyticsHelpers"
import { Resource } from "@/constants/permission"
import { getAnalyticsRefusalExcel } from "@/http/analytics"
import classes from "@/pages/Analytics/Common/Analytics.module.scss"
import AnalyticsHeader from "@/pages/Analytics/Common/AnalyticsHeader"
import AnalyticsTitle from "@/pages/Analytics/Common/AnalyticsTitle"

import { orderRefusalColumns } from "./AnalyticsRefusalPageColumns"

import { useRefusalsAnalyticsQuery } from "@/hook/Analytics/useAnalyticsQuery"
import { useDownloadFile } from "@/hook/useDownloadFile"
import { useTable } from "@/hook/useTable"

import { FilterType, IAnalyticsPageDisplayUnit, IRefusalAnalytics } from "@/types/IAnalytics"
import { IDictionary, ITableColumn } from "@/types/ui"

const { Title } = Typography

const displayUnits = [
  {
    value: "refusalCount",
    label: "Количество",
    bigLabel: "Количество отказов"
  },
  {
    value: "refusalAmount",
    label: "Тонны",
    bigLabel: "Объем отказов (Тонны)"
  },
  {
    value: "refusalSum",
    label: "Рубли",
    bigLabel: "Сумма отказов"
  },
  {
    value: "refusalSumMillions",
    label: "Рубли (млн)",
    bigLabel: "Сумма отказов (млн)"
  }
]

const additionalDataKeys: IDictionary<IAnalyticsPageDisplayUnit> = {
  "refusalCount": {
    value: "orderCount",
    label: "",
    bigLabel: "Количество заказов"
  },
  "refusalAmount": {
    value: "orderAmount",
    label: "",
    bigLabel: "Объем заказов (Тонны)"
  },
  "refusalSum": {
    value: "orderSum",
    label: "",
    bigLabel: "Сумма заказов"
  },
  "refusalSumMillions": {
    value: "orderSumMillions",
    label: "",
    bigLabel: "Сумма заказов (млн)"
  },
  "deliverySum": {
    value: "pickupSum",
    label: "",
    bigLabel: "Сумма самовывозов"
  },
  "deliverySumMillions": {
    value: "pickupSumMillions",
    label: "",
    bigLabel: "Сумма самовывозов (млн)"
  }
}

const AnalyticsRefusalPage: FC = () => {
  const { optionsList, filterComponent, filtersComponentRef, isComparison, filterInfoAsText, getFilterInfoAsPlainText } = useAnalyticsFilters({
    baseDisplayUnits: [],
    additionalDisplayUnits: displayUnits
  })

  const [initialColumns, setInitialColumns] =
    useState<ITableColumn<IRefusalAnalytics>[]>(orderRefusalColumns(true))

  const [analyticsMapped, setAnalyticsMapped] = useState<IRefusalAnalytics[]>([])
  const [forOnlyRefusalDiagramData, setForOnlyRefusalDiagramData] = useState<IRefusalAnalytics[]>([])
  const [mappedComparisonData, setMappedComparisonData] = useState<IRefusalAnalytics[]>([])
  const [displayUnit, setDisplayUnit] = useState<IAnalyticsPageDisplayUnit>(displayUnits[0])


  const additionalDataKey = additionalDataKeys[displayUnit.value]

  const { columns, pagination, filter, order } = useTable({
    initialColumns: initialColumns
  })

  useEffect(() => {
    setInitialColumns(orderRefusalColumns(!isComparison))
  }, [isComparison])

  const { data: analyticsRefusalData, isLoading: isLoadingAnalyticsRefusal } = useRefusalsAnalyticsQuery({
      optionsList
    },
    {
      Page: pagination.page,
      PageSize: pagination.pageSize,
      Filter: filter.filter,
      OrderBy: order.orderBy
    })

  useEffect(() => {
    if (analyticsRefusalData?.response.data) {
      setAnalyticsMapped(analyticsRefusalData.response.data.map(r => ({
        rowKey: crypto.randomUUID(),
        period: r.period,
        refusalCount: r.refusalCount,
        refusalAmount: r.refusalAmount,
        refusalSum: r.refusalSum,
        refusalSumMillions: Number((r.refusalSum / 1000000).toFixed(3)),
        orderCount: r.orderCount,
        orderAmount: r.orderAmount,
        orderSum: r.orderSum,
        orderSumMillions: Number((r.orderSum / 1000000).toFixed(3))
      })))
    }
  }, [analyticsRefusalData])

  useEffect(() => {
    if (analyticsMapped && analyticsMapped.length > 0) {
      setTotalNumberAnalytics(getTotalNumberAnalytics(analyticsMapped))

      setForOnlyRefusalDiagramData(isComparison ? analyticsMapped : analyticsMapped.filter(r => r.refusalCount > 0))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyticsMapped, isComparison])

  useEffect(() => {
    if(isComparison && forOnlyRefusalDiagramData.length === 2) { // при сравнении отдает только 2 "строки" для каждого из "периодов"
      const filtersAsText = getFilterInfoAsPlainText([FilterType.Period])

        const comparisonData = forOnlyRefusalDiagramData.map((d, index) => {
          const newVal = { ...d }
          newVal.filterText = `${filtersAsText[index]}`
          return newVal
        })
        setMappedComparisonData(comparisonData)
    }
    else {
      setMappedComparisonData([])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forOnlyRefusalDiagramData, isComparison])

  const { getTotalNumberAnalytics } = useAnalyticsHelpers({})
  const [totalNumberAnalytics, setTotalNumberAnalytics] = useState<ITotalNumberAnalytics[]>([])
  const mainColor = "#8884d8"
  const secondColor = "#387908"

  useEffect(() => {
    // Сбросить фильтры при монтировании компонента, чтобы не было конфликтов с другими страницами
    filtersComponentRef.current?.cancelFilters()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { download: excelDownload, isLoading: isExcelLoading } =
    useDownloadFile(() =>
      getAnalyticsRefusalExcel({
          optionsList
        },
        columns.data,
        {
          Page: pagination.page,
          PageSize: pagination.pageSize,
          Filter: filter.filter,
          OrderBy: order.orderBy
        })
    )

  return (
    <div>
      <AnalyticsTitle 
        text={"Динамика отказов"}
      />
      <AnalyticsHeader 
        displayUnits={displayUnits}
        activeUnit={displayUnit}
        filterComponent={filterComponent}
        onChangeTabs={setDisplayUnit}
        onExcelDownload={{
          onExcelDownload: excelDownload,
          isExcelLoading: isExcelLoading
        }}
      />
      <Card bordered={false} className={ classes.cardWithTabs }>
        <Title level={4}>Отказы за период</Title>
        <Row>
          {
            !isComparison &&
              <AnalyticsAreaChart dataKeys={[displayUnit]} data={forOnlyRefusalDiagramData} filtersText={filterInfoAsText}/>
          }
          {
            isComparison &&
              <AnalyticsBarChart
                  dataKeys={[displayUnit]}
                  data={mappedComparisonData}/>
          }
        </Row>

        <Title level={4}>Отказы в сравнении с количеством заказов за период</Title>
        <Row>
          {
            !isComparison && (
              <AnalyticsLineChart data={analyticsMapped}
                dataKeys={[displayUnit, additionalDataKey]}
                filtersText={filterInfoAsText}
                colors={[secondColor]}
                showDots={true}
              />
            )
          }
          {
            isComparison &&
              <AnalyticsBarChart
                  dataKeys={[displayUnit, additionalDataKey]}
                  data={mappedComparisonData}/>
          }
        </Row>

        <Title level={4}>Общее количество заказов и отказов</Title>
        {!isComparison && (
          <Row>
            <AnalyticsPieChart
              chartWidth={730}
              chartHeight={450}
              dataKey={"count"}
              name={"name"}
              data={[
                { count: totalNumberAnalytics.find(x => x.key === displayUnit.value)?.value, name: displayUnit.bigLabel },
                { count: totalNumberAnalytics.find(x => x.key === additionalDataKey.value)?.value, name: additionalDataKey.bigLabel }
              ]}
              nameKey="name"
              fill={mainColor}
              fillCells={[secondColor, mainColor]}/>
          </Row>
        )}
        <Table
          dataSource={analyticsMapped}
          columns={columns.data}
          isSelectable={false}
          rowKey="rowKey"
          isLoading={isLoadingAnalyticsRefusal}
          pagination={{
            currentPage: pagination.page,
            pageSize: pagination.pageSize,
            total: analyticsRefusalData?.response.count,
            onPageChange: pagination.onPageChange,
            onPageSizeChange: pagination.onPageSizeChange
          }}
          onSorterChange={order.setOrderBy}
          permission={{
            resource: Resource.RefusalsAnalytics
          }}
        />
      </Card>
    </div>
  )
}

export default AnalyticsRefusalPage